import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Send, Check } from 'lucide-react';

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1759134155377-4207d89b39ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
};

// TikTok icon component
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.15 } }
};

const contactInfo = [
  {
    icon: Phone,
    title: 'Phone',
    content: '+44 7000 000000',
    link: 'tel:+447000000000'
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'hello@unathi.co.uk',
    link: 'mailto:hello@unathi.co.uk'
  },
  {
    icon: MapPin,
    title: 'Address',
    content: '123 High Street\nLondon, UK',
    link: 'https://maps.google.com'
  },
];

const openingHours = [
  { day: 'Monday', hours: '9:00 AM - 7:00 PM' },
  { day: 'Tuesday', hours: '9:00 AM - 7:00 PM' },
  { day: 'Wednesday', hours: '9:00 AM - 7:00 PM' },
  { day: 'Thursday', hours: '9:00 AM - 7:00 PM' },
  { day: 'Friday', hours: '9:00 AM - 7:00 PM' },
  { day: 'Saturday', hours: '9:00 AM - 6:00 PM' },
  { day: 'Sunday', hours: '10:00 AM - 4:00 PM' },
];

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Frontend only - just show success message
    setIsSubmitted(true);
  };

  return (
    <main data-testid="contact-page">
      {/* Page Header */}
      <section 
        className="page-header"
        style={{ 
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-[#0A0A0A]" />
        <div className="container-custom relative z-10 text-center pt-24">
          <motion.p 
            className="text-[#C9A227] uppercase tracking-[0.3em] text-sm mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Get in Touch
          </motion.p>
          <motion.h1 
            className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#F5F5F5]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Contact <span className="italic">Us</span>
          </motion.h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.div className="mb-12" variants={fadeInUp}>
                <h2 className="font-heading text-3xl md:text-4xl text-[#F5F5F5] mb-4">
                  We'd Love to Hear from <span className="italic text-[#C9A227]">You</span>
                </h2>
                <p className="text-[#999] leading-relaxed">
                  Have a question about our services? Want to book an appointment? 
                  Reach out to us through any of the channels below.
                </p>
              </motion.div>

              {/* Contact Cards */}
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <motion.a
                    key={info.title}
                    href={info.link}
                    target={info.title === 'Address' ? '_blank' : undefined}
                    rel={info.title === 'Address' ? 'noopener noreferrer' : undefined}
                    className="flex items-start gap-4 p-6 bg-white/5 border border-white/10 hover:border-[#C9A227]/50 transition-colors group"
                    variants={fadeInUp}
                    data-testid={`contact-${info.title.toLowerCase()}`}
                  >
                    <div className="w-12 h-12 border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-colors">
                      <info.icon size={20} className="text-[#C9A227] group-hover:text-black transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-[#F5F5F5] mb-1">{info.title}</h3>
                      <p className="text-[#999] whitespace-pre-line">{info.content}</p>
                    </div>
                  </motion.a>
                ))}
              </div>

              {/* Opening Hours */}
              <motion.div 
                className="p-6 bg-white/5 border border-white/10"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-3 mb-6">
                  <Clock size={20} className="text-[#C9A227]" />
                  <h3 className="font-heading text-xl text-[#F5F5F5]">Opening Hours</h3>
                </div>
                <div className="space-y-3">
                  {openingHours.map((item) => (
                    <div key={item.day} className="flex justify-between text-sm">
                      <span className="text-[#F5F5F5]">{item.day}</span>
                      <span className="text-[#999]">{item.hours}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Social Links */}
              <motion.div className="mt-8" variants={fadeInUp}>
                <h3 className="font-heading text-lg text-[#F5F5F5] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon"
                    data-testid="contact-instagram"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon"
                    data-testid="contact-facebook"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                  <a 
                    href="https://tiktok.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="social-icon"
                    data-testid="contact-tiktok"
                    aria-label="TikTok"
                  >
                    <TikTokIcon size={20} />
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={stagger}
            >
              {isSubmitted ? (
                <motion.div 
                  className="success-message h-full flex flex-col items-center justify-center text-center"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="contact-success-message"
                >
                  <div className="w-16 h-16 mb-6 rounded-full bg-[#C9A227] flex items-center justify-center">
                    <Check size={32} className="text-black" />
                  </div>
                  <h3 className="font-heading text-2xl text-[#F5F5F5] mb-4">Message Sent!</h3>
                  <p className="text-[#999] mb-8">
                    Thank you for contacting UNATHI. A member of our team will respond shortly.
                  </p>
                  <button
                    onClick={() => { setIsSubmitted(false); setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="btn-secondary"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  onSubmit={handleSubmit}
                  className="bg-white/5 border border-white/10 p-8 md:p-12"
                  variants={fadeInUp}
                  data-testid="contact-form"
                >
                  <h3 className="font-heading text-2xl text-[#F5F5F5] mb-8">Send Us a Message</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm text-[#F5F5F5] uppercase tracking-wider mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="form-input form-input-dark"
                        placeholder="Your full name"
                        data-testid="contact-input-name"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-[#F5F5F5] uppercase tracking-wider mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="form-input form-input-dark"
                          placeholder="your@email.com"
                          data-testid="contact-input-email"
                        />
                      </div>

                      <div>
                        <label className="block text-sm text-[#F5F5F5] uppercase tracking-wider mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input form-input-dark"
                          placeholder="+44 7000 000000"
                          data-testid="contact-input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-[#F5F5F5] uppercase tracking-wider mb-2">
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        className="form-input form-input-dark"
                        placeholder="What is this about?"
                        data-testid="contact-input-subject"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-[#F5F5F5] uppercase tracking-wider mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="form-input form-input-dark resize-none"
                        placeholder="Tell us how we can help..."
                        data-testid="contact-input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn-primary w-full flex items-center justify-center gap-2"
                      data-testid="contact-submit-btn"
                    >
                      <Send size={16} />
                      Send Message
                    </button>
                  </div>
                </motion.form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-[#0A0A0A]" data-testid="map-section">
        <div className="container-custom pb-24">
          <div className="map-container">
            <div className="text-center">
              <MapPin size={48} className="text-[#C9A227] mx-auto mb-4" />
              <p className="text-[#999]">Map placeholder - Google Maps integration available</p>
              <p className="text-[#666] text-sm mt-2">123 High Street, London, UK</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
